import type { TasksKanbanTask } from '@ts-tasks-kanban/interfaces'
import type { KanbanAgent } from '@ts-tutor-kanban/interfaces'
import type { TasksKanbanStage } from '@brighterly/lib-core-types/tasks-kanban/enums'
import type { ExtraFieldsValues } from '@ts-lists/types'

import { TasksKanbanRelatedToType, TasksKanbanTeam } from '@ts-tasks-kanban/enums'

import { useTasksKanbanStore } from '@stores/tasks-kanban'
import { useCommonStore } from '@stores/common'

import { useAdminRoutes } from '@composables/routes/admin-route.hook'

import { getAxiosErrorMessage } from '@helpers/axios'

import { useToast } from '@UI/Toast/ToastProvider'

interface KanbanTaskShowApiOptions {
  id: number | string
}

type KanbanTaskShowResponse = TasksKanbanTask & {
  creator: KanbanAgent | null
}

const isRelatedToType = (value: string | null): value is TasksKanbanRelatedToType =>
  !!value && Object.values(TasksKanbanRelatedToType).includes(value as TasksKanbanRelatedToType)

export const useKanbanTaskShowApi = async (
  options: KanbanTaskShowApiOptions,
): Promise<KanbanTaskShowResponse | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const commonStore = useCommonStore()
  const tasksKanbanStore = useTasksKanbanStore()

  const url = useAdminRoutes('admin.kanban-tasks.show', { id: options.id })

  commonStore.setLoading(true)

  try {
    const { data } = await $axios.get(url!)
    const task: KanbanTaskShowResponse = data.data

    tasksKanbanStore.setTaskType(task.task_type ?? null)
    tasksKanbanStore.setTeam((task.team as TasksKanbanTeam) ?? null)
    tasksKanbanStore.setStage((task.status as TasksKanbanStage) ?? null)
    tasksKanbanStore.setPriority(task.priority ?? null)
    tasksKanbanStore.setDeadline(task.deadline ?? null)
    tasksKanbanStore.setCreatedAt(task.created_at ?? null)
    tasksKanbanStore.setUpdatedAt(task.updated_at ?? null)
    tasksKanbanStore.setAssignee(task.assignee ?? null)
    tasksKanbanStore.setCreator(task.creator ?? null)
    tasksKanbanStore.setCustomer(task.customer ?? null)
    tasksKanbanStore.setWaitingReason(task.waiting_reason ?? null)
    tasksKanbanStore.setResolution(task.resolution ?? null)
    tasksKanbanStore.setCustomerOutcome(task.customer_outcome ?? null)
    tasksKanbanStore.setCancelReason(task.cancel_reason ?? null)

    if (task.related_to && isRelatedToType(task.related_to_type)) {
      tasksKanbanStore.setRelatedTo({
        id: task.related_to.id,
        type: task.related_to_type,
        code: task.related_to.code,
        kidName: task.related_to.kid?.name,
      })
    } else {
      tasksKanbanStore.setRelatedTo(null)
    }

    if (task.task_type && task.extra_fields) {
      tasksKanbanStore.setExtraFieldsForTaskType({
        taskType: task.task_type,
        values: task.extra_fields as ExtraFieldsValues,
      })
    }

    commonStore.setLoading(false)

    return task
  } catch (err: unknown) {
    console.error(err)
    toast.error(getAxiosErrorMessage(err))
    commonStore.setLoading(false)

    return null
  }
}
